import { MaterialsFactory } from '../factories/MaterialsFactory';
import { FontsStorage } from '../factories/FontsStorage';
import { TextShiftMode } from '../../../constants/UnitTypes';

const createGeometry = require('three-bmfont-text');

const THREE = require('three');

const randomShift = (glyphs) => {
    const shifts = new Float32Array(glyphs.length * 4);
    let i = -1;

    glyphs.forEach(() => {
        const shift = 2 * Math.random();

        shifts[i += 1] = shift;
        shifts[i += 1] = shift;
        shifts[i += 1] = shift;
        shifts[i += 1] = shift;
    });

    return shifts;
};

const randomShiftLine = (glyphs) => {
    const shifts = new Float32Array(glyphs.length * 4);
    let i = -1;

    const shiftMap = new Map();

    glyphs.forEach((glyph) => {
        const { line } = glyph;
        const shift = shiftMap.has(line) ? shiftMap.get(line) : -1 + 2 * Math.random();

        shiftMap.set(line, shift);

        shifts[i += 1] = shift;
        shifts[i += 1] = shift;
        shifts[i += 1] = shift;
        shifts[i += 1] = shift;
    });

    return shifts;
};

class Text3D extends THREE.Group {
    constructor(config, text, webgl, customMaterial = null) {
        super();

        const { width } = config;

        this.config = config;
        this.text = text;
        this.webgl = webgl;
        this._width = width;
        this._hasAnimation = false;
        this._customMaterial = null;
        this._progress = 0;

        if (customMaterial) {
            const { material, isAnimation } = customMaterial;

            this._hasAnimation = !!material && isAnimation;
            this._customMaterial = material;
        }

        const fontBmf = this._getFont();

        if (fontBmf) {
            const fontInfoSize = Math.abs(fontBmf.font.info.size);

            const geom = this._getGeometry(config, fontBmf, fontInfoSize);
            const matrl = this._getMaterial(fontBmf);
            const mesh = new THREE.Mesh(geom, matrl);

            this._mesh = mesh;
            this._material = matrl;

            this.add(mesh);
            this._updatePosition(config, fontInfoSize);
        }
    }

    _getFont(fontFamily2) {
        const { fontFamily } = this.config;

        return FontsStorage.get(fontFamily2 || fontFamily);
    }

    _getGeometry(config, fontBmf, fontInfoSize) {
        const {
            fontSize, lineHeight, letterSpacing, tabSize, align, isUpperCase, hasShiftProgress, shiftMode
        } = config;
        let { text } = this;

        const { fontSizeFix } = fontBmf;
        const scaleFont = fontSize / (fontInfoSize * fontSizeFix);

        const params = {};

        if (typeof lineHeight !== 'undefined') params.lineHeight = lineHeight * fontInfoSize;
        if (typeof letterSpacing !== 'undefined') params.letterSpacing = letterSpacing * fontInfoSize;
        if (typeof tabSize !== 'undefined') params.tabSize = tabSize;

        if (isUpperCase) text = text.toUpperCase();

        const textGeometry = createGeometry({
            font: fontBmf.font,
            text,
            width: this.width / scaleFont,
            flipY: fontBmf.atlas.flipY,
            align: align ?? 'center',
            ...params
        });

        if (hasShiftProgress) {
            const { visibleGlyphs, layout } = textGeometry;
            const randomShiftArr = shiftMode === TextShiftMode.LINE
                ? randomShiftLine(visibleGlyphs ?? layout.glyphs)
                : randomShift(visibleGlyphs ?? layout.glyphs);

            textGeometry.setAttribute('shift', new THREE.BufferAttribute(randomShiftArr, 1));
        }

        return textGeometry;
    }

    _getMaterial(fontBmf) {
        return MaterialsFactory.create(
            { ...this.config, atlas: fontBmf?.atlas },
            this._customMaterial ? this._customMaterial.type : 'textMaterial2'
        );
    }

    _updatePosition(config, fontInfoSize) {
        if (!this._mesh) return;

        const scale = this.webgl.getTextScale(
            fontInfoSize,
            config.fontSize
        );

        const { layout } = this._mesh.geometry;

        this._mesh.scale.set(scale, -scale, 1);
        this._mesh.position.set(
            (-layout.width / 2) * scale,
            (-layout.height / 2) * scale,
            0
        );

        this._scale = scale;
    }

    invalidate(t) {
        if (!this._hasAnimation) return;

        this._material.uniforms.time.value = t;
        this._material.uniformsNeedUpdate = true;
    }

    set width(value) {
        if (value === this._width) return this._width;

        // Update width if static variant won't work

        this._width = value;

        return this._width;
    }

    get width() {
        return this._width;
    }

    get mesh() {
        return this._mesh;
    }

    get layout() {
        if (this._mesh) return this._mesh.geometry.layout;

        return null;
    }

    get lineHeight() {
        return this.layout ? this.layout.lineHeight * this._scale : 0;
    }

    get textHeight() {
        return this.layout ? this.layout.height * this._scale : 0;
    }

    get textWidth() {
        return this.layout ? this.layout.width * this._scale : 0;
    }

    get textInnerWidth() {
        if (!this.layout) return 0;

        const { visibleGlyphs } = this._mesh.geometry;

        visibleGlyphs.sort((a, b) => a.position[0] - b.position[0]);

        const first = visibleGlyphs[0];
        const last = visibleGlyphs[visibleGlyphs.length - 1];

        return (last.position[0]
            + last.data.xoffset
            + last.data.width * 0.5
            - first.position[0]
            - first.data.xoffset
            + first.data.width * 0.5) * this._scale;
    }

    set progress(value) {
        if (this._progress === value) return;

        this._progress = value;

        if (!this._material) return;

        this._material.uniforms.uShiftInfluence.value = value;
        this._material.uniformsNeedUpdate = true;
    }

    get progress() {
        return this._progress;
    }

    get material() {
        return this._material;
    }

    setMetrics(m) {
        if (!this._mesh) return;

        const config = { ...this.config, ...m };
        const fontBmf = this._getFont();
        const fontInfoSize = Math.abs(fontBmf.font.info.size);

        this._width = config.width;

        this._mesh.geometry = this._getGeometry(config, fontBmf, fontInfoSize);
        this._updatePosition(config, fontInfoSize);
        this.config = config;
    }
}

export default Text3D;
